import type { RewardConditionGroupProgressData, RewardConditionProgressData } from '@/schemas/v2/user'

/**
 * 獲得条件ツリーの構築（#3581）。
 *
 * バックエンドは末端条件（conditions[]）と選択グループ（conditionGroups[]）を別配列で返すため、
 * groupId / parentId を辿って親子構造を復元する。
 *
 * ⚠️ ツリーの段数に上限は設けない。
 *
 * バックエンドの App\Service\v2\RewardConditionTree::MAX_DEPTH は現状 3 だが、
 * 将来引き上げられる可能性がある。ここで段数を打ち切ると、
 * バックエンドが先に上限を上げたときに「フロント側の定数を直し忘れた分の条件が黙って消える」
 * という気付きにくい不具合になるため、段数では打ち切らない。
 *
 * 再帰の安全弁は「段数」ではなく「parentId のループ検出」で持たせる
 * （無限再帰を防ぐのに必要十分で、正常なデータは段数がいくつでも壊れない）。
 * REWARD_CONDITION_MAX_DEPTH のような段数上限の定数は定義しないこと。
 */

/** 選択グループのノード。子条件をぶら下げる */
export type RewardConditionGroupNode = {
  kind: 'group'
  /** Mantine Tree の value（一意）。グループは `g-{id}` */
  key: string
  /** グループ表示名。管理画面で未設定なら null（表示側で operator から既定文言を組み立てる） */
  displayName: string | null
  /** 子条件の結合方法 */
  operator: 'AND' | 'OR'
  /** 達成済みの子条件数（分子）。OR では threshold を超えることがある */
  current: number
  /** 達成に必要な子条件数（分母） */
  threshold: number
  /** 1 始まりの段数。表示用の情報であり、分岐条件には使わない */
  depth: number
  children: RewardConditionTreeNode[]
}

/** 末端条件のノード */
export type RewardConditionLeafNode = {
  kind: 'condition'
  /** 末端条件は API 側に ID が無いため conditions[] のインデックスから採番する */
  key: string
  condition: RewardConditionProgressData
  /** 1 始まりの段数。表示用の情報であり、分岐条件には使わない */
  depth: number
  children: []
}

export type RewardConditionTreeNode = RewardConditionGroupNode | RewardConditionLeafNode

/**
 * parentId を辿ってグループの段数と「実際にぶら下げる親」を確定させる。
 *
 * 2 つの異常系を区別して扱う:
 *
 * - **循環**（A→B→A や自己参照）: トップレベル扱い（`resolvedParentId = null`）にして
 *   完全に切り離す。ここで親に繋ぐと children が相互参照して
 *   **オブジェクトグラフ自体が循環し**、描画時の再帰でスタックオーバーフローする。
 * - **祖先の欠落**（先祖のどこかが conditionGroups[] に居ない）: 辿るのをそこで打ち切るだけで、
 *   **直近の親が存在するならそこにはぶら下げる**。欠落した祖先の位置でツリーが分かれるだけで、
 *   その下の親子関係まで崩す必要はない（切り離された祖先に繋いでも循環は生じない）。
 *
 * ⚠️ 呼び出し側は必ずこの resolvedParentId を使うこと。生の parentId を使うと循環データで壊れる。
 */
function resolveGroupPlacement(
  group: RewardConditionGroupProgressData,
  groupById: Map<number, RewardConditionGroupProgressData>,
): { depth: number; resolvedParentId: number | null } {
  const visited = new Set<number>([group.id])
  let depth = 1
  let parentId = group.parentId

  while (parentId !== null) {
    const parent = groupById.get(parentId)
    // 祖先が居ない（子条件0件などで出力されなかった）。ここから上は辿れないので打ち切る
    if (parent === undefined) {
      break
    }
    // 訪問済みに戻った = 循環。安全側に倒して完全に切り離す
    if (visited.has(parent.id)) {
      return { depth: 1, resolvedParentId: null }
    }
    visited.add(parent.id)
    depth += 1
    parentId = parent.parentId
  }

  const hasDirectParent = group.parentId !== null && groupById.has(group.parentId)
  return { depth, resolvedParentId: hasDirectParent ? group.parentId : null }
}

/**
 * 子を1つも持たないグループを取り除く（再帰）。
 *
 * バックエンドは段数を超えたグループの配下を conditions[]/conditionGroups[] の
 * どちらにも出さない一方、その親グループ自体は出力する。そのまま描くと
 * 「開けないのに未達成の行」が出てしまい、末端条件と見分けもつかないため落とす。
 */
function pruneEmptyGroups(nodes: RewardConditionTreeNode[]): RewardConditionTreeNode[] {
  const result: RewardConditionTreeNode[] = []
  for (const node of nodes) {
    if (node.kind === 'condition') {
      result.push(node)
      continue
    }
    node.children = pruneEmptyGroups(node.children)
    if (node.children.length > 0) result.push(node)
  }
  return result
}

/**
 * conditions[] と conditionGroups[] から獲得条件ツリーを組み立てる。
 *
 * 並び順（API の配列順をそのまま使う）:
 * - グループどうし: conditionGroups[] の配列順
 * - 末端条件どうし: conditions[] の配列順
 * - 同じ親に末端条件と子グループが混在する場合: 「末端条件 → 子グループ」
 *   （API が別配列で返すため相対順序は復元できない）
 *
 * 防御的な扱い:
 * - conditionGroups が空 → 末端条件だけの 1 段リスト
 * - groupId が conditionGroups[] に無い → その条件はトップレベルに退避（表示欠落を防ぐ）
 * - parentId が循環している → 循環を構成するグループはトップレベル扱い
 *   （これが唯一の再帰ガード。段数では打ち切らない）
 * - conditions[] に CONDITION_GROUP 行が混ざっている → 除外する
 *   （現行バックエンドは出さないが、スキーマ上は取りうる。旧バックエンド互換のため
 *   conditionGroups を optional にしているのと同じ理由で、来た場合に備える）
 * - conditionGroups[] に同じ id が複数ある → 最初の 1 件だけ使う
 * - 子を 1 つも持たないグループ → 取り除く（pruneEmptyGroups）
 */
export function buildRewardConditionTree(
  conditions: RewardConditionProgressData[],
  conditionGroups: RewardConditionGroupProgressData[],
): RewardConditionTreeNode[] {
  // 重複 id は「最初の1件が勝つ」で統一する。
  // ここを後勝ちにすると、ノード自体は最初の行から作る一方で祖先を辿るときだけ
  // 最後の行の parentId を見ることになり、depth がずれる
  //（depth は太字・アコーディオン・中黒の判定に使うので描画結果が変わる）。
  const groupById = new Map<number, RewardConditionGroupProgressData>()
  for (const group of conditionGroups) {
    if (!groupById.has(group.id)) groupById.set(group.id, group)
  }

  // 1. グループノードを作りつつ、段数と実際にぶら下げる親を確定させる
  const nodeById = new Map<number, RewardConditionGroupNode>()
  const placementById = new Map<number, number | null>()
  for (const group of conditionGroups) {
    // 同じ id が複数あると同一ノードを2回ぶら下げてしまい、key 重複と開閉状態の共有が起きる
    if (nodeById.has(group.id)) continue
    const { depth, resolvedParentId } = resolveGroupPlacement(group, groupById)
    nodeById.set(group.id, {
      kind: 'group',
      key: `g-${group.id}`,
      displayName: group.displayName,
      operator: group.operator,
      current: group.current,
      threshold: group.threshold,
      depth,
      children: [],
    })
    placementById.set(group.id, resolvedParentId)
  }

  const roots: RewardConditionTreeNode[] = []

  // 2. 末端条件を先に配置する（同じ親の中で「末端条件 → 子グループ」の順にするため）。
  //    グループ行は conditionGroups[] 側で扱うのでここでは除外する。
  conditions.forEach((condition, index) => {
    if (condition.type === 'CONDITION_GROUP') return
    const parent =
      condition.groupId !== null && condition.groupId !== undefined ? nodeById.get(condition.groupId) : undefined
    const leaf: RewardConditionLeafNode = {
      kind: 'condition',
      key: `c-${index}`,
      condition,
      // 親が居なければトップレベル（groupId が conditionGroups[] に無いケースを含む）
      depth: parent !== undefined ? parent.depth + 1 : 1,
      children: [],
    }
    if (parent !== undefined) {
      parent.children.push(leaf)
    } else {
      roots.push(leaf)
    }
  })

  // 3. グループを親にぶら下げる。必ず resolvedParentId を使うこと（循環対策）
  const attached = new Set<number>()
  for (const group of conditionGroups) {
    const node = nodeById.get(group.id)
    // 重複 id は 2 周目以降をスキップ（同じノードを2回ぶら下げない）
    if (node === undefined || attached.has(group.id)) continue
    attached.add(group.id)

    const resolvedParentId = placementById.get(group.id) ?? null
    const parent = resolvedParentId !== null ? nodeById.get(resolvedParentId) : undefined
    if (parent !== undefined) {
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }

  // 4. 子を持たないグループを落とす
  return pruneEmptyGroups(roots)
}

/**
 * 末端条件が達成済みか。
 * 判定規則はバックエンドの UserRewardStatusEvaluator::walkConditionProgress() に揃える
 * （LOGIN_DAYS 等の未対応種別は current が常に 0 なので、種別で明示的に false にする）。
 */
export function isConditionAchieved(condition: RewardConditionProgressData): boolean {
  return (
    (condition.type === 'STAMP_COUNT' || condition.type === 'POINT_SUM') && condition.current >= condition.threshold
  )
}

/**
 * グループが達成済みか。
 * threshold が 0 のグループは未達成扱い（バックエンドの `required >= 1 && achieved >= required` と一致）。
 */
export function isGroupAchieved(node: RewardConditionGroupNode): boolean {
  return node.threshold >= 1 && node.current >= node.threshold
}

/** ツリーノードの達成状態。グループ／末端条件の違いを吸収する */
export function isTreeNodeAchieved(node: RewardConditionTreeNode): boolean {
  return node.kind === 'group' ? isGroupAchieved(node) : isConditionAchieved(node.condition)
}
